import { c as _c } from "react/compiler-runtime";
import { IcoSmartphone40 } from '@onefootprint/icons';
import type { Language, Locale, OnboardingSessionConfig } from '@onefootprint/request-types';
import { PageShell } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import type { DeviceInfo } from '@/idv2/utils/get-device';
import { getLogger } from '@/idv2/utils/logger';
import useCreateHandoffUrl from '../hooks/use-create-handoff-url';
import useD2pSession from '../hooks/use-d2p-session';
import useRequirementsTitle from '../hooks/use-requirements-title';
import type { TransferRequirements } from '../types';
import ContinueOnDesktop from './continue-on-desktop';
import ProcessingOverlay from './processing-overlay';
import QrCodeDisplay from './qr-code-display';
const {
  logInfo
} = getLogger({
  name: 'idv2',
  location: 'qr-transfer'
});
type QrTransferProps = {
  authToken: string;
  config: OnboardingSessionConfig;
  device: DeviceInfo;
  l10n: {
    locale?: Locale;
    language: Language;
  };
  missingRequirements: TransferRequirements;
  sandboxIdDocOutcome?: string;
  onContinueOnDesktop: () => void;
  fromContinueOnMobile: boolean;
  onDone: () => void;
};
const QrTransfer = t0 => {
  const $ = _c(55);
  const {
    authToken,
    config,
    device,
    l10n,
    missingRequirements,
    sandboxIdDocOutcome,
    onContinueOnDesktop,
    fromContinueOnMobile,
    onDone
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "transfer.qrTransfer"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t,
    i18n
  } = useTranslation("idv2", t1);
  let t2;
  if ($[1] !== missingRequirements) {
    t2 = {
      missingRequirements,
      isContinuingOnDesktop: false,
      isAppClipProcessing: false
    };
    $[1] = missingRequirements;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    title,
    subtitle
  } = useRequirementsTitle(t2);
  let t3;
  if ($[3] !== authToken || $[4] !== device || $[5] !== l10n || $[6] !== onDone || $[7] !== sandboxIdDocOutcome) {
    t3 = {
      authToken,
      device,
      l10n,
      sandboxIdDocOutcome,
      onFinish: onDone
    };
    $[3] = authToken;
    $[4] = device;
    $[5] = l10n;
    $[6] = onDone;
    $[7] = sandboxIdDocOutcome;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const d2p = useD2pSession(t3);
  let t4;
  if ($[9] !== config || $[10] !== d2p.state.scopedAuthToken || $[11] !== fromContinueOnMobile || $[12] !== i18n.language || $[13] !== missingRequirements) {
    t4 = {
      authToken: d2p.state.scopedAuthToken,
      language: i18n.language,
      missingRequirements,
      onboardingConfig: config,
      fromContinueOnMobile
    };
    $[9] = config;
    $[10] = d2p.state.scopedAuthToken;
    $[11] = fromContinueOnMobile;
    $[12] = i18n.language;
    $[13] = missingRequirements;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const handoffUrl = useCreateHandoffUrl(t4);
  const isLoading = d2p.state.isGenerating || !handoffUrl || !d2p.state.scopedAuthToken;
  const isProcessing = d2p.state.status === "in_progress";
  let t5;
  if ($[15] !== missingRequirements.documents) {
    t5 = missingRequirements.documents.some(_temp);
    $[15] = missingRequirements.documents;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const shouldHideContinueOnDesktop = t5;
  let t6;
  if ($[17] !== d2p) {
    t6 = () => {
      d2p.cancel();
      d2p.regenerate();
      logInfo("Transfer cancelled");
    };
    $[17] = d2p;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleCancel = t6;
  if (isProcessing) {
    let t7;
    if ($[19] !== t) {
      t7 = t(_temp2);
      $[19] = t;
      $[20] = t7;
    } else {
      t7 = $[20];
    }
    let t8;
    if ($[21] !== d2p.state.isCanceling || $[22] !== handleCancel || $[23] !== t7 || $[24] !== title) {
      t8 = <ProcessingOverlay title={title} subtitle={t7} isCanceling={d2p.state.isCanceling} onCancel={handleCancel} />;
      $[21] = d2p.state.isCanceling;
      $[22] = handleCancel;
      $[23] = t7;
      $[24] = title;
      $[25] = t8;
    } else {
      t8 = $[25];
    }
    return t8;
  }
  const SmsButtonWithCountdown = _temp3;
  let t7;
  if ($[26] !== subtitle || $[27] !== t) {
    t7 = true ? t(_temp4) : subtitle;
    $[26] = subtitle;
    $[27] = t;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  let t8;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <IcoSmartphone40 color="primary" />;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const t9 = !shouldHideContinueOnDesktop;
  let t10;
  if ($[30] !== t9) {
    t10 = cx("w-full relative before:content-[''] before:absolute before:top-0 before:left-1/2 before:w-[150%] before:h-px before:-translate-x-1/2 before:border-t before:border-dashed before:border-[var(--border-color-tertiary)]", {
      "after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:w-[150%] after:h-px after:-translate-x-1/2 after:border-t after:border-dashed after:border-[var(--border-color-tertiary)]": t9
    });
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== d2p.state.scopedAuthToken || $[33] !== handoffUrl) {
    t11 = false && <SmsButtonWithCountdown authToken={d2p.state.scopedAuthToken} handoffUrl={handoffUrl} />;
    $[32] = d2p.state.scopedAuthToken;
    $[33] = handoffUrl;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t) {
    t12 = true ? undefined : t(_temp5);
    $[35] = t;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== handoffUrl || $[38] !== isLoading || $[39] !== t12) {
    t13 = <QrCodeDisplay text={t12} qrValue={handoffUrl} isLoading={isLoading} />;
    $[37] = handoffUrl;
    $[38] = isLoading;
    $[39] = t12;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  let t14;
  if ($[41] !== t10 || $[42] !== t11 || $[43] !== t13) {
    t14 = <div className={t10}>{t11}{t13}</div>;
    $[41] = t10;
    $[42] = t11;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== onContinueOnDesktop || $[46] !== shouldHideContinueOnDesktop) {
    t15 = !shouldHideContinueOnDesktop && <ContinueOnDesktop onClick={onContinueOnDesktop} />;
    $[45] = onContinueOnDesktop;
    $[46] = shouldHideContinueOnDesktop;
    $[47] = t15;
  } else {
    t15 = $[47];
  }
  let t16;
  if ($[48] !== t14 || $[49] !== t15) {
    t16 = <div className="flex flex-col items-center gap-6">{t14}{t15}</div>;
    $[48] = t14;
    $[49] = t15;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== t16 || $[52] !== t7 || $[53] !== title) {
    t17 = <PageShell title={title} subtitle={t7} headerIcon={t8}>{t16}</PageShell>;
    $[51] = t16;
    $[52] = t7;
    $[53] = title;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  return t17;
};
export default QrTransfer;
function _temp(document) {
  return document.uploadSettings === "require_mobile_capture";
}
function _temp2($) {
  return $.processingSubtitle;
}
function _temp3(_props) {
  return null;
}
function _temp4($_0) {
  return $_0.qrCode.instructionsNoSms;
}
function _temp5($_1) {
  return $_1.qrCode.instructions;
}